import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { FC } from 'react';
import {
  AIAssistantProvider,
  useAIAssistantContext,
} from 'src/hooks/AIAssistant/useAiAssistantContext';
import { themeColors } from 'src/theme';
import styles from './AIAssistantPage.module.scss';
import { AIContentCreator } from './components/AIContentCreator';
import { AIContentLibrary } from './components/AIContentLibrary';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-tabpanel-${index}`}
      aria-labelledby={`ai-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AIAssistantContent: FC = () => {
  const { activeTab, switchToTab, resetFormStates } = useAIAssistantContext();
  const isMobile = useMediaQuery('(max-width: 600px)');

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0 && activeTab !== 0) {
      resetFormStates();
    }
    switchToTab(newValue);
  };

  const goToContentLibrary = () => {
    switchToTab(1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>AI Assistant</h1>
        <p>Generate and manage AI-powered video content</p>
      </div>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            aria-label="ai assistant tabs"
            TabIndicatorProps={{ sx: { background: themeColors.text.dark100 } }}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
            sx={{
              '& button': { color: themeColors.text.dark70 },
              '& button.Mui-selected': { color: themeColors.text.dark100 },
            }}
            centered={!isMobile}
          >
            <Tab label="Create Content" id="ai-tab-0" />
            <Tab label="Content Library" id="ai-tab-1" />
          </Tabs>
        </Box>
        <CustomTabPanel value={activeTab} index={0}>
          <AIContentCreator goToContentLibrary={goToContentLibrary} />
        </CustomTabPanel>
        <CustomTabPanel value={activeTab} index={1}>
          <AIContentLibrary />
        </CustomTabPanel>
      </Box>
    </div>
  );
};

export const AIAssistantPage: FC = () => {
  return (
    <AIAssistantProvider>
      <AIAssistantContent />
    </AIAssistantProvider>
  );
};
